import React from 'react';

import pick from 'lodash/pick';

import { Theme } from '@lumapps/lumx/react';
import { LINK_ELEMENTS } from '@lumapps/wrex-link/plugin/markdown/deserialize';
import { LIST_ELEMENTS } from '@lumapps/wrex-list/plugin/markdown/deserialize';
import { TABLE_ELEMENTS } from '@lumapps/wrex-table/plugin/markdown/deserialize';
import { TYPOGRAPHY_ELEMENTS, TYPOGRAPHY_MARKS } from '@lumapps/wrex-typography/plugin/markdown/deserialize';
import { ContentView, ContentViewProps } from '@lumapps/wrex/components/ContentView';
import { fromMarkdown } from '@lumapps/wrex/serialization/markdown/fromMarkdown';
import type { DeserializeOptions } from '@lumapps/wrex/serialization/markdown/fromMarkdown/types';

import { WREX_MARKDOWN_ELEMENTS, WREX_MARKDOWN_MARKS } from '../../constants';

const MARKDOWN_RENDER_OPTIONS: DeserializeOptions = {
    /** Mark converters */
    marks: {
        ...pick(TYPOGRAPHY_MARKS, ['strong', 'emphasis', 'delete']),
    },
    /** Element converters */
    elements: {
        ...pick(TYPOGRAPHY_ELEMENTS, ['paragraph', 'heading', 'code', 'inlineCode']),
        ...LIST_ELEMENTS,
        ...LINK_ELEMENTS,
        ...TABLE_ELEMENTS,
    },
    customElements: {},
};

export interface MarkdownContentViewProps {
    /** The markdown text that will be converted to slate and displayed */
    markdown?: string;
    /** Props to pass to the ContentView */
    contentViewProps?: Omit<Partial<ContentViewProps>, 'elements' | 'marks' | 'content'>;
}

const CLASSNAME = 'wrex-markdown-content-view';

/** Display a content view limited for markdown elements
 * This component allow you to display rich markdown text
 * Note: If you need to add custom markdown (like user mention), use the ContentView instead
 * @example 'My **important** text' will render a paragraph with the word important in bold
 */
export const MarkdownContentView: React.FC<MarkdownContentViewProps> = ({ markdown, contentViewProps }) => {
    if (!markdown) {
        return null;
    }

    return (
        <ContentView
            theme={Theme.light}
            className={CLASSNAME}
            {...contentViewProps}
            content={fromMarkdown(markdown, undefined, MARKDOWN_RENDER_OPTIONS)}
            elements={WREX_MARKDOWN_ELEMENTS}
            marks={WREX_MARKDOWN_MARKS}
        />
    );
};
